import React from 'react';

/**
 * Shared dialog shell.
 *
 * Default form (title + padded body) is unchanged — every existing caller keeps
 * working without edits.
 *
 * Optional `header` / `footer` / `bodyClassName` let a caller supply its own
 * chrome, which the Modify-Parties wizard needs: its header carries the
 * Add/Remove toggle and a step caption, and its footer carries Back/Next. That
 * mirrors the mediator portal's Dialog, whose header/footer stay fixed while
 * only the body scrolls.
 */
export default function Modal({
  isOpen,
  onClose,
  title,
  children,
  width = 'max-w-lg',
  header,
  footer,
  bodyClassName = 'px-6 py-5',
  overlayClassName = 'bg-black/50',
}) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center">
      <div className={`absolute inset-0 ${overlayClassName}`} onClick={onClose} />
      <div
        className={`relative bg-white rounded-2xl shadow-xl ${width} w-full mx-4 max-h-[88vh] flex flex-col`}
      >
        {/* Header — caller-supplied, else the default title bar. */}
        {header !== undefined ? (
          <div className="flex-shrink-0">{header}</div>
        ) : (
          <div className="flex-shrink-0 flex items-center justify-between px-6 py-5 border-b border-gray-100">
            <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        )}

        {/* Only the body scrolls, so header and footer stay put on long steps. */}
        <div className={`flex-1 overflow-y-auto ${bodyClassName}`}>{children}</div>

        {footer !== undefined && <div className="flex-shrink-0">{footer}</div>}
      </div>
    </div>
  );
}
